// Air quality (EPA AirNow). The AQI scale, colors and advice follow EPA's
// published AQI guidance.

import type { SourceStamp } from "./models";

export interface AqReading {
  /** "O3", "PM2.5" or "PM10". */
  parameter: string;
  aqi: number;
  category: number;
}

export interface AqForecastDay {
  date: string;
  aqi: number | null;
  category: number;
  parameter: string;
  actionDay: boolean;
  discussion: string | null;
}

export type AirPayload =
  | { status: "unavailable"; reason: string }
  | { status: "ok"; reportingArea: string | null; observedAt: string | null; readings: AqReading[]; forecast: AqForecastDay[]; stamp: SourceStamp };

export const AQI_CATEGORIES: Record<number, { name: string; color: string; text: string; advice: string }> = {
  1: { name: "Good", color: "#00e400", text: "#0b2a0b", advice: "Air quality is satisfactory. Enjoy being outside." },
  2: { name: "Moderate", color: "#ffff00", text: "#2a2a00", advice: "Acceptable. Unusually sensitive people may want to take it easier outdoors." },
  3: { name: "Unhealthy for sensitive groups", color: "#ff7e00", text: "#2a1400", advice: "Children, older adults and people with asthma or heart disease should limit long or hard outdoor activity." },
  4: { name: "Unhealthy", color: "#ff0000", text: "#ffffff", advice: "Everyone may feel effects. Keep outdoor activity short; sensitive groups should stay in." },
  5: { name: "Very unhealthy", color: "#8f3f97", text: "#ffffff", advice: "Health alert: everyone should avoid long outdoor activity. Keep windows closed." },
  6: { name: "Hazardous", color: "#7e0023", text: "#ffffff", advice: "Emergency conditions: everyone should stay indoors." },
};

export function aqiCategory(aqi: number): number {
  if (aqi <= 50) return 1;
  if (aqi <= 100) return 2;
  if (aqi <= 150) return 3;
  if (aqi <= 200) return 4;
  if (aqi <= 300) return 5;
  return 6;
}

export const POLLUTANT_NAMES: Record<string, string> = { O3: "Ozone", "PM2.5": "Fine particles (smoke, dust)", PM10: "Coarse particles (dust)", CO: "Carbon monoxide", NO2: "Nitrogen dioxide" };

/** Short labels for pollutant chips. */
export const POLLUTANT_SHORT: Record<string, string> = { O3: "Ozone", "PM2.5": "PM2.5", PM10: "PM10", CO: "CO", NO2: "NO₂" };
